import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { Close, Sync } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { updateOrganization } from '../../../../../redux/services';
import {
  userOrganization,
  useToast,
  useUpdateUIState,
} from '../../../../../redux/action';
import {
  Container,
  Header,
  Content,
  SaveBtn,
  Title,
  Group,
} from '../EditAPIConnectionDrawer/EditAPIConnectionDrawer.styled';
import { Title as ContentTitle } from '../../GeneralInfo/GeneralInfo.styled';
import {
  Text,
  FitContent,
  ListContainer,
  SmallText,
} from './EditSyncSettingsDrawer.styled';

const EditSyncSettingsDrawer = ({
  selectedOrg,
  isEditSyncSettingsDrawerVisible,
  setEditSyncSettingsDrawerVisibility,
  orgTypes,
}) => {
  const dispatch = useDispatch();
  const { errorToast, successToast } = useToast();
  const { fullLoader } = useUpdateUIState();
  const [syncedStatuses, setSyncStatuses] = useState(
    selectedOrg?.assocStatus || ''
  );
  const [syncedMembershipNames, setSyncedMembershipNames] = useState([]);
  const [stateMembershipNames, setStateMembershipNames] = useState([]);
  const [excludedSubtypes, setExcludedSubtypes] = useState(
    selectedOrg?.excludingBillTypes || []
  );
  const [phoneType, setPhoneType] = useState(selectedOrg?.phoneType || '');
  const [coeCategory, setCOECategory] = useState(
    selectedOrg?.magicCOECategory || ''
  );
  const [coeEnabled, setCOEEnabled] = useState(
    selectedOrg?.magicCOEEnabled || false
  );
  const [eventsEnabled, setEventsEnabled] = useState(
    selectedOrg?.eventsEnabled || false
  );
  const [
    skipEventSyncOfInactiveRegistrants,
    setSkipEventSyncOfInactiveRegistrants,
  ] = useState(
    eventsEnabled ? selectedOrg?.skipEventSyncOfInactiveRegistrants : false
  );
  const [memberTypeAsSubtype, setMemberTypeAsSubtype] = useState(
    selectedOrg?.searchByMemberType || false
  );
  const [syncOnlyActiveContacts, setSyncOnlyActiveContacts] = useState(
    selectedOrg?.gzSyncConfig?.syncOnlyActiveContacts ?? false
  );
  const [allowNotOwnedMemberships, setAllowNotOwnedMemberships] = useState(
    selectedOrg?.gzSyncConfig?.allowNotOwnedMemberships ?? false
  );
  const [isEventsSyncVisible, setEventsSyncVisibility] = useState(false);
  const [isCOECategoryVisible, setCOECategoryVisible] = useState(false);
  const ramcoTypeId = orgTypes?.find(orgType => orgType.name === 'ramco')?.id;
  const isRamcoOrg = selectedOrg?.orgType === ramcoTypeId;
  const gzTypeId = orgTypes?.find(orgType => orgType.name === 'gz')?.id;
  const isGZOrg = selectedOrg?.orgType === gzTypeId;

  useEffect(() => {
    if (orgTypes && orgTypes.length) {
      const magicId = orgTypes.find(orgType => orgType.name === 'magic')?.id;
      setEventsSyncVisibility(magicId === selectedOrg.orgType);
      setCOECategoryVisible(magicId === selectedOrg.orgType);
    }
  }, [orgTypes]);

  useEffect(() => {
    if (selectedOrg?.memberships?.length) {
      const memberships = selectedOrg.memberships;
      const membershipNames = memberships.map(membership => membership.name);
      setSyncedMembershipNames(membershipNames);
    }
    if (selectedOrg?.stateMemberships?.length) {
      const names = selectedOrg.stateMemberships.map(m => m.name);
      setStateMembershipNames(names);
    }
  }, [selectedOrg]);

  useEffect(() => {
    if (!eventsEnabled) {
      setSkipEventSyncOfInactiveRegistrants(false);
    }
  }, [eventsEnabled]);

  const closeDrawer = () => {
    setEditSyncSettingsDrawerVisibility(false);
  };

  const handleSubmit = async () => {
    try {
      fullLoader(true, 'Updating organization sync settings.');

      const date = new Date();

      const memberships = formatUpdatedMemberships(syncedMembershipNames);
      const stateMemberships = isRamcoOrg
        ? formatUpdatedMemberships(
            stateMembershipNames,
            selectedOrg?.stateMemberships
          )
        : undefined;

      const orgData = {
        id: selectedOrg.id,
        eventsEnabled,
        skipEventSyncOfInactiveRegistrants,
        searchByMemberType: memberTypeAsSubtype,
        assocStatus: trimArrayValues(syncedStatuses),
        associationIDs: trimArrayValues(syncedMembershipNames),
        memberships,
        excludingBillTypes: trimArrayValues(excludedSubtypes),
        ...(isRamcoOrg && { stateMemberships }),
        updatedDate: date.toString(),
        orgType: selectedOrg.orgType,
        importMagic: true, //To ensure the AMS syncs will be enabled
        magicCOECategory: coeCategory,
        magicCOEEnabled: coeEnabled,
        phoneType: phoneType.trim(),
      };

      if (isGZOrg) {
        orgData.gzSyncConfig = {
          syncOnlyActiveContacts,
          allowNotOwnedMemberships,
        };
      }

      const updatedOrg = await updateOrganization(orgData);
      if (!updatedOrg) {
        errorToast('Failed to update organization sync settings.');
        fullLoader(false);
        return;
      }

      dispatch(userOrganization(updatedOrg));
      successToast('Successfully updated organization AMS sync settings.');

      closeDrawer();
      fullLoader(false);
    } catch (e) {
      console.error(e);
      errorToast('Error updating organization AMS sync settings.');
      fullLoader(false);
    }
  };

  const trimArrayValues = values => {
    if (!values || !values.length) return [];
    return values.map(val => val.trim());
  };

  const splitStringToArray = inputString =>
    inputString ? inputString.split(';') : [];

  const formatUpdatedMemberships = (
    membershipNames,
    existingMemberships = selectedOrg?.memberships || []
  ) => {
    if (!membershipNames || !membershipNames.length) return [];

    const existingMembershipMap = {};
    for (const membership of existingMemberships) {
      existingMembershipMap[membership.name] = membership;
    }

    //Merge existing and new memberships
    return membershipNames.map(membershipName => {
      const cleanName = membershipName.trim();
      const existingMembership = existingMembershipMap[cleanName];
      return existingMembership
        ? existingMembership
        : {
            name: cleanName,
            amsId: cleanName,
          };
    });
  };

  return (
    <Drawer
      anchor={'right'}
      onClose={closeDrawer}
      open={isEditSyncSettingsDrawerVisible}
    >
      <Container>
        <Header>
          <Group>
            <IconButton onClick={closeDrawer} style={{ color: 'white' }}>
              <Close />
            </IconButton>
            <Title>Editing: Sync Settings</Title>
          </Group>
          <SaveBtn onClick={handleSubmit}>Save</SaveBtn>
        </Header>
        <Content>
          <ContentTitle>
            <Sync size="small" />
            Sync Settings
          </ContentTitle>
          <SmallText>
            Multiple values must be separated by semicolons.
          </SmallText>
          <TextField
            label="Synced Statuses"
            variant="outlined"
            size="small"
            value={syncedStatuses?.join(';')}
            onChange={e => setSyncStatuses(splitStringToArray(e.target.value))}
          />
          <TextField
            label="Synced Memberships"
            variant="outlined"
            size="small"
            value={syncedMembershipNames?.join(';')}
            onChange={e =>
              setSyncedMembershipNames(splitStringToArray(e.target.value))
            }
          />
          {isRamcoOrg && (
            <TextField
              label="State Memberships"
              variant="outlined"
              size="small"
              value={stateMembershipNames?.join(';')}
              onChange={e =>
                setStateMembershipNames(splitStringToArray(e.target.value))
              }
            />
          )}
          <TextField
            label="Excluded Subtypes"
            variant="outlined"
            size="small"
            value={excludedSubtypes?.join(';')}
            onChange={e =>
              setExcludedSubtypes(splitStringToArray(e.target.value))
            }
          />
          <TextField
            label="Synced Phone Field"
            variant="outlined"
            size="small"
            value={phoneType}
            onChange={e => setPhoneType(e.target.value)}
          />
          <ListContainer gap={'5px'}>
            <Text>Additional Settings</Text>
            <FitContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={memberTypeAsSubtype}
                    color="primary"
                    onChange={e => setMemberTypeAsSubtype(e.target.checked)}
                  />
                }
                size="small"
                label="Use Member Types as Subtypes"
              />
            </FitContent>
            {isGZOrg && (
              <FitContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={syncOnlyActiveContacts}
                      color="primary"
                      onChange={e =>
                        setSyncOnlyActiveContacts(e.target.checked)
                      }
                    />
                  }
                  label="Enable syncing only active status contacts. This is not the same as membership status. By default, customers do NOT enable this."
                />
              </FitContent>
            )}
            {isGZOrg && (
              <FitContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={allowNotOwnedMemberships}
                      color="primary"
                      onChange={e =>
                        setAllowNotOwnedMemberships(e.target.checked)
                      }
                    />
                  }
                  label="Enable syncing contact memberships that are not owned by the contact. Uncommon, but some customers want this."
                />
              </FitContent>
            )}
            {isEventsSyncVisible && (
              <FitContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={eventsEnabled}
                      color="primary"
                      onChange={e => setEventsEnabled(e.target.checked)}
                    />
                  }
                  label="Enable Events Sync"
                />
              </FitContent>
            )}
            {eventsEnabled ? (
              <FitContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={skipEventSyncOfInactiveRegistrants}
                      color="primary"
                      onChange={e =>
                        setSkipEventSyncOfInactiveRegistrants(e.target.checked)
                      }
                    />
                  }
                  label="Skip Sync of Inactive Event Registrants"
                />
              </FitContent>
            ) : (
              ''
            )}
            {isCOECategoryVisible ? (
              <FitContent>
                <FormControlLabel
                  control={
                    <Switch
                      checked={coeEnabled}
                      color="primary"
                      onChange={e => setCOEEnabled(e.target.checked)}
                    />
                  }
                  label="Enable COE features (Rap only)"
                />
              </FitContent>
            ) : (
              ''
            )}
            {isCOECategoryVisible && coeEnabled && (
              <TextField
                label="Rapattoni COE Category (Member Education)"
                variant="outlined"
                size="small"
                value={coeCategory}
                onChange={e => setCOECategory(e.target.value)}
                style={{ marginTop: '10px' }}
              />
            )}
          </ListContainer>
        </Content>
      </Container>
    </Drawer>
  );
};

export default EditSyncSettingsDrawer;
